import { useEffect, useRef, useState, useCallback } from 'react';
import './Store.css';
import ProductCard from '../components/ProductCard';
import PreorderModal from '../components/PreorderModal/PreorderModal';
import { useCart } from '../context/CartContext';
//import Carousel from "../components/Carousel";

const LIMIT = 24;

function Store() {
  const { addToCart } = useCart();

  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const featured = Array.isArray(products)
    ? products.filter(p => p.featured)
    : [];

  const art = Array.isArray(products)
    ? products.filter(p => p.type === "art")
    : [];

  const storeItems = Array.isArray(products)
    ? products.filter(p => p.type !== "art")
    : [];

  const loaderRef = useRef(null);
  const fetchingRef = useRef(false);

  // Single fetch function that handles both pagination and filtering
  const fetchProducts = useCallback(async (pageToLoad, selectedCategory, reset = false) => {
    if (fetchingRef.current) return;
    
    // Don't fetch if no more products and not resetting
    if (!reset && !hasMore && pageToLoad > 1) return;

    fetchingRef.current = true;
    setLoading(true);

    try {
      const categoryParam = selectedCategory === 'all' ? '' : `&type=${selectedCategory}`;
      const res = await fetch(
        `/api/products?page=${pageToLoad}&limit=${LIMIT}${categoryParam}`
      );
      const data = await res.json();

      // Handle both response formats
      let items = [];
      let hasMoreFlag = false;

      if (Array.isArray(data)) {
        // Direct array format (NearlyFreeSpeech)
        items = data;
        hasMoreFlag = data.length === LIMIT;
      } else if (data.products && Array.isArray(data.products)) {
        // Wrapped object format (local)
        items = data.products;
        hasMoreFlag = data.hasMore || false;
      } else {
        items = [];
        hasMoreFlag = false;
      }

      if (reset) {
        setProducts(items);
      } else {
        setProducts((prev) => [...prev, ...items]);
      }

      setPage(pageToLoad);
      setHasMore(hasMoreFlag);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, [hasMore]); // Add hasMore as dependency

  // Initial load - runs once
  useEffect(() => {
    fetchProducts(1, category, true);
  }, []); // Empty dependency array - only runs once on mount

  // Handle category changes - reset everything
  useEffect(() => {
    // Reset state
    setProducts([]);
    setHasMore(true);
    setPage(1);
    fetchingRef.current = false;
    // Fetch first page of new category
    fetchProducts(1, category, true);
  }, [category]); // Only depends on category

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !fetchingRef.current && hasMore && !loading) {
          fetchProducts(page + 1, category, false);
        }
      },
      { threshold: 0.1 }
    );

    const current = loaderRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.disconnect();
    };
  }, [fetchProducts, hasMore, page, category, loading]);

  const handleFilter = (type) => {
    setCategory(type);
  };

  const handlePreorderClick = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  return (
    <div className="store-page">
      <a href="/" className="back-home-btn">← Back to Home</a>
      <h2>Browse Our Full Collection</h2>

      <div className="filter-bar">
        {['all', 'album', 'novel', 'art', 'tshirt', 'cap', 'hoodie'].map((type) => (
          <button
            key={type}
            onClick={() => handleFilter(type)}
            className={category === type ? 'active' : ''}
          >
            {type === 'all'
              ? 'All'
              : type === 'album'
              ? 'Albums'
              : type === 'novel'
              ? 'Novels'
              : type === 'art'
              ? 'Art'
              : type === 'cap'
              ? 'Caps'
              : type === 'tshirt'
              ? 'Tshirts'
              : type === 'hoodie'
              ? 'Hoodies'
              : type.charAt(0).toUpperCase() + type.slice(1) + 's'}
          </button>
        ))}
      </div>

      <div className="product-grid">
        {products.map((p) => (
          <ProductCard
            key={p.id}
            product={p}
            onPreorder={() => handlePreorderClick(p)}
            onAddToCart={() => addToCart(p)}
          />
        ))}
      </div>

      <div ref={loaderRef} style={{ minHeight: '60px' }}>
        {loading && <p>Loading more...</p>}
        {!hasMore && products.length > 0 && <p>No more products</p>}
      </div>

      {showModal && selectedProduct && (
        <PreorderModal
          product={selectedProduct}
          onClose={() => {
            setShowModal(false);
            setSelectedProduct(null);
          }}
        />
      )}
    </div>
  );
}

export default Store;